import { useEffect, useMemo, useState, type FormEvent } from "react";
import AdminModal from "./AdminModal";
import ImageUploader from "@/components/base/ImageUploader";
import MediaLibraryModal from "@/components/feature/MediaLibraryModal";
import {
  createAdminBusiness,
  updateAdminBusiness,
  getAdminBusinessDetail,
  getAdminCategories,
  getAdminSubcategories,
  getAdminLocations,
  getAdminUsers,
  getAdminPlans,
  type CreateAdminBusinessInput,
  type UpdateAdminBusinessInput,
} from "@/services/adminService";
import type {
  AdminBusiness,
  AdminBusinessStatus,
  AdminCategory,
  AdminLocation,
  AdminUser,
  AdminPlan,
} from "@/types/admin";
import {
  adminBtnPrimary,
  adminBtnGhost,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";
import {
  LISTING_COVER_RULE,
  LISTING_LOGO_RULE,
  LISTING_GALLERY_RULE,
} from "@/utils/imageValidation";
import { resolvePlanLimits } from "@/utils/plans";

interface BusinessFormModalProps {
  mode: "create" | "edit";
  token: string | null;
  /** Summary row — required in edit mode. */
  business?: AdminBusiness;
  /** Current listings, used for the duplicate-name warning. */
  existing: AdminBusiness[];
  onClose: () => void;
  onSaved: (row: AdminBusiness) => void;
}

const SELECT_CLASS = `${adminInput} cursor-pointer appearance-none`;

interface FormState {
  name: string;
  tagline: string;
  description: string;
  categoryId: string;
  subcategoryId: string;
  districtId: string;
  townId: string;
  planId: string;
  ownerId: string;
  status: AdminBusinessStatus;
  phone: string;
  whatsapp: string;
  email: string;
  website: string;
  address: string;
  pinCode: string;
  videoUrl: string;
  coverUrl: string;
  logoUrl: string;
  gallery: string[];
}

const EMPTY: FormState = {
  name: "",
  tagline: "",
  description: "",
  categoryId: "",
  subcategoryId: "",
  districtId: "",
  townId: "",
  planId: "",
  ownerId: "",
  status: "pending",
  phone: "",
  whatsapp: "",
  email: "",
  website: "",
  address: "",
  pinCode: "",
  videoUrl: "",
  coverUrl: "",
  logoUrl: "",
  gallery: [],
};

const CREATE_STATUS: { value: AdminBusinessStatus; label: string; icon: string }[] = [
  { value: "pending", label: "Save as Pending", icon: "ri-time-line" },
  { value: "active", label: "Publish as Active", icon: "ri-checkbox-circle-line" },
];

const EDIT_STATUS: { value: AdminBusinessStatus; label: string; icon: string }[] = [
  { value: "active", label: "Active", icon: "ri-checkbox-circle-line" },
  { value: "pending", label: "Pending", icon: "ri-time-line" },
  { value: "rejected", label: "Rejected", icon: "ri-close-circle-line" },
  { value: "suspended", label: "Suspended", icon: "ri-pause-circle-line" },
];

/** Resolve a select value by matching a display name when an id is missing. */
function matchIdBy<T extends { id: number }>(list: T[], predicate: (item: T) => boolean): string {
  const found = list.find(predicate);
  return found ? String(found.id) : "";
}

/**
 * Admin-only listing form. Handles both creating a new listing and editing an
 * existing one, with a duplicate-name warning so the same business is never
 * added twice by accident.
 */
export default function BusinessFormModal({
  mode,
  token,
  business,
  existing,
  onClose,
  onSaved,
}: BusinessFormModalProps) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState<FormState>(EMPTY);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [subcategories, setSubcategories] = useState<AdminCategory[]>([]);
  const [locations, setLocations] = useState<AdminLocation[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dupAck, setDupAck] = useState(false);
  const [picker, setPicker] = useState<null | "cover" | "logo" | "gallery">(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cats, subs, locs, usrs, planBundle] = await Promise.all([
          getAdminCategories(token),
          getAdminSubcategories(token),
          getAdminLocations(token),
          getAdminUsers(token),
          getAdminPlans(token),
        ]);
        if (!alive) return;
        setCategories(cats);
        setSubcategories(subs);
        setLocations(locs);
        setUsers(usrs);
        setPlans(planBundle.plans);

        const districts = locs.filter((l) => l.type === "district");

        if (isEdit && business) {
          const detail = await getAdminBusinessDetail(token, business.id);
          if (!alive) return;
          setForm({
            name: detail.name,
            tagline: detail.tagline ?? "",
            description: detail.description ?? "",
            categoryId: detail.categoryId
              ? String(detail.categoryId)
              : matchIdBy(cats, (c) => c.name === detail.categoryName),
            subcategoryId: detail.subcategoryId ? String(detail.subcategoryId) : "",
            districtId: detail.districtId
              ? String(detail.districtId)
              : matchIdBy(districts, (d) => d.name === detail.districtName),
            townId: detail.townId
              ? String(detail.townId)
              : matchIdBy(
                  locs.filter((l) => l.type === "town"),
                  (t) => t.name === detail.townName,
                ),
            planId: detail.planId
              ? String(detail.planId)
              : matchIdBy(planBundle.plans, (p) => p.slug === detail.planSlug),
            ownerId: detail.ownerId
              ? String(detail.ownerId)
              : matchIdBy(usrs, (u) => u.email === detail.ownerEmail),
            status: detail.status,
            phone: detail.phone ?? "",
            whatsapp: detail.whatsapp ?? "",
            email: detail.email ?? "",
            website: detail.website ?? "",
            address: detail.address ?? "",
            pinCode: detail.pinCode ?? "",
            videoUrl: detail.videoUrl ?? "",
            coverUrl: detail.coverUrl ?? "",
            logoUrl: detail.logoUrl ?? "",
            gallery: detail.gallery,
          });
        } else {
          const free = planBundle.plans.find((p) => p.slug === "free") ?? planBundle.plans[0];
          if (free) setForm((prev) => ({ ...prev, planId: String(free.id) }));
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : "Could not load the listing form.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token, isEdit, business]);

  const districts = locations.filter((l) => l.type === "district");
  const selectedDistrict = districts.find((d) => String(d.id) === form.districtId);
  const towns = locations.filter(
    (l) => l.type === "town" && (!selectedDistrict || l.districtName === selectedDistrict.name),
  );
  const subOptions = subcategories.filter((s) => String(s.parentId) === form.categoryId);
  const selectedCategory = categories.find((c) => String(c.id) === form.categoryId);
  const selectedDistrictRow = districts.find((d) => String(d.id) === form.districtId);
  const selectedTown = towns.find((t) => String(t.id) === form.townId);
  const selectedOwner = users.find((u) => String(u.id) === form.ownerId);
  const selectedPlan = plans.find((p) => String(p.id) === form.planId);

  // The gallery cap follows whichever plan is selected, so an admin editing on
  // someone's behalf can never set more photos than that plan actually allows.
  const planLimits = resolvePlanLimits({
    slug: selectedPlan?.slug,
    name: selectedPlan?.name,
    tagline: selectedPlan?.tagline,
    highlights: selectedPlan?.highlights,
  });
  const photoLimit = Math.max(1, planLimits.photos);
  // Never force-trim photos a listing already holds (e.g. after a plan change).
  const galleryCap = Math.max(photoLimit, form.gallery.length);
  const overPhotoLimit = form.gallery.length > photoLimit;

  /** Other listings that share this exact name (case-insensitive). */
  const nameMatches = useMemo(() => {
    const normalized = form.name.trim().toLowerCase();
    if (normalized.length < 3) return [];
    return existing.filter(
      (b) => b.id !== business?.id && b.name.trim().toLowerCase() === normalized,
    );
  }, [existing, form.name, business]);

  const districtKey = (selectedDistrictRow?.name ?? "").trim().toLowerCase();

  /**
   * Same name AND same district — almost certainly the very same business.
   * This is the hard warning that needs an explicit confirmation.
   */
  const exactDuplicates = useMemo(
    () =>
      nameMatches.filter(
        (b) =>
          districtKey !== "" &&
          (b.districtName ?? "").trim().toLowerCase() === districtKey,
      ),
    [nameMatches, districtKey],
  );

  /** Same name but a different area — only a softer heads-up, never blocks. */
  const similarNames = useMemo(
    () => nameMatches.filter((b) => !exactDuplicates.includes(b)),
    [nameMatches, exactDuplicates],
  );

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onNameChange(value: string) {
    setForm((prev) => ({ ...prev, name: value }));
    setDupAck(false);
  }

  function onCategoryChange(value: string) {
    setForm((prev) => ({ ...prev, categoryId: value, subcategoryId: "" }));
  }

  function onDistrictChange(value: string) {
    setForm((prev) => ({ ...prev, districtId: value, townId: "" }));
  }

  /** Build the summary row the Businesses table renders. */
  function toSummaryRow(): AdminBusiness {
    const base: AdminBusiness = {
      id: business?.id ?? 0,
      name: form.name.trim(),
      slug: business?.slug ?? "",
      categoryName: selectedCategory?.name ?? "—",
      districtName: selectedDistrictRow?.name ?? "—",
      townName: selectedTown?.name ?? null,
      ownerName: selectedOwner?.name ?? "—",
      ownerEmail: selectedOwner?.email ?? "—",
      planSlug: selectedPlan?.slug ?? "free",
      status: form.status,
      isFeatured: business?.isFeatured ?? false,
      isSponsored: business?.isSponsored ?? false,
      isVerified: business?.isVerified ?? false,
      videoUrl: form.videoUrl.trim() || null,
      views: business?.views ?? 0,
      rating: business?.rating ?? 0,
      reviewCount: business?.reviewCount ?? 0,
      createdAt: business?.createdAt ?? new Date().toISOString(),
    };
    return base;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError("");

    if (!form.name.trim()) { setError("Business name is required."); return; }
    if (!form.categoryId) { setError("Please choose a category."); return; }
    if (!form.districtId) { setError("Please choose a district."); return; }
    if (exactDuplicates.length > 0 && !dupAck) {
      setError(
        `A listing named "${form.name.trim()}" already exists in ${selectedDistrictRow?.name ?? "this district"}. Tick the confirmation box to add it anyway.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && business) {
        const payload: UpdateAdminBusinessInput = {
          name: form.name.trim(),
          tagline: form.tagline.trim() || null,
          description: form.description.trim() || null,
          categoryId: Number(form.categoryId),
          subcategoryId: form.subcategoryId ? Number(form.subcategoryId) : null,
          districtId: Number(form.districtId),
          townId: form.townId ? Number(form.townId) : null,
          planId: form.planId ? Number(form.planId) : null,
          ownerId: form.ownerId ? Number(form.ownerId) : null,
          status: form.status,
          phone: form.phone.trim() || null,
          whatsapp: form.whatsapp.trim() || null,
          email: form.email.trim() || null,
          website: form.website.trim() || null,
          address: form.address.trim() || null,
          pinCode: form.pinCode.trim() || null,
          videoUrl: form.videoUrl.trim() || null,
          logoUrl: form.logoUrl || null,
          coverUrl: form.coverUrl || null,
          gallery: form.gallery,
        };
        await updateAdminBusiness(token, business.id, payload);
      } else {
        const payload: CreateAdminBusinessInput = {
          name: form.name.trim(),
          categoryId: Number(form.categoryId),
          subcategoryId: form.subcategoryId ? Number(form.subcategoryId) : null,
          districtId: Number(form.districtId),
          townId: form.townId ? Number(form.townId) : null,
          planId: form.planId ? Number(form.planId) : null,
          ownerId: form.ownerId ? Number(form.ownerId) : null,
          status: form.status === "active" ? "active" : "pending",
          tagline: form.tagline.trim() || undefined,
          description: form.description.trim() || undefined,
          address: form.address.trim() || undefined,
          pinCode: form.pinCode.trim() || undefined,
          phone: form.phone.trim() || undefined,
          whatsapp: form.whatsapp.trim() || undefined,
          email: form.email.trim() || undefined,
          website: form.website.trim() || undefined,
          videoUrl: form.videoUrl.trim() || undefined,
          logoUrl: form.logoUrl || undefined,
          coverUrl: form.coverUrl || undefined,
          gallery: form.gallery.length ? form.gallery : undefined,
        };
        const { id, slug } = await createAdminBusiness(token, payload);
        onSaved({ ...toSummaryRow(), id, slug });
        return;
      }
      onSaved(toSummaryRow());
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Could not ${isEdit ? "update" : "create"} the listing. Please try again.`,
      );
      setSubmitting(false);
    }
  }

  const statusChoices = isEdit ? EDIT_STATUS : CREATE_STATUS;

  return (
    <>
      <AdminModal
        wide
        title={isEdit ? "Edit listing" : "Create new listing"}
        subtitle={
          isEdit
            ? business?.name ?? "Update this listing's details."
            : "Publish a business on the owner's behalf, or add one directly."
        }
        onClose={onClose}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={adminBtnGhost}>Cancel</button>
            <button type="submit" form="business-form" disabled={submitting || loading} className={adminBtnPrimary}>
              {submitting
                ? <><i className="ri-loader-4-line animate-spin text-sm" /> Saving…</>
                : isEdit
                  ? <><i className="ri-save-line text-sm" /> Save changes</>
                  : <><i className="ri-add-line text-sm" /> Create listing</>}
            </button>
          </div>
        }
      >
        <form id="business-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-base" /> Loading categories, locations and plans…
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2.5 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />{error}
            </div>
          )}

          {/* ── Business details ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={adminLabel} htmlFor="bf-name">Business name *</label>
              <input id="bf-name" className={adminInput} value={form.name}
                onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Kamath Hardware" required />

              {exactDuplicates.length > 0 && (
                <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-alert-line text-sm" />
                    </span>
                    A listing with this name already exists in {selectedDistrictRow?.name ?? "this district"}
                  </p>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {exactDuplicates.slice(0, 4).map((d) => (
                      <li key={d.id} className="text-xs text-amber-800">
                        <a href={`/business/${d.slug}`} target="_blank" rel="noopener noreferrer"
                          className="font-medium underline underline-offset-2 hover:text-amber-900">
                          {d.name}
                        </a>
                        <span className="text-amber-700"> · {d.categoryName} · {d.townName ?? d.districtName} · {d.status}</span>
                      </li>
                    ))}
                  </ul>
                  <label className="mt-2.5 flex cursor-pointer items-start gap-2 text-xs text-amber-900">
                    <input type="checkbox" checked={dupAck} onChange={(e) => setDupAck(e.target.checked)}
                      className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-amber-600" />
                    <span>I understand — create this as a separate listing anyway.</span>
                  </label>
                </div>
              )}

              {similarNames.length > 0 && (
                <div className="mt-2 rounded-lg border border-background-300 bg-background-100 px-3.5 py-3">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-700">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-information-line text-sm" />
                    </span>
                    A similar name exists in another area
                  </p>
                  <ul className="mt-1.5 flex flex-col gap-1">
                    {similarNames.slice(0, 4).map((d) => (
                      <li key={d.id} className="text-xs text-foreground-600">
                        <a href={`/business/${d.slug}`} target="_blank" rel="noopener noreferrer"
                          className="font-medium underline underline-offset-2 hover:text-foreground-900">
                          {d.name}
                        </a>
                        <span className="text-foreground-500"> · {d.districtName} · {d.status}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1.5 text-[11px] text-foreground-500">
                    Different area, so this won't be flagged as a duplicate — just double-checking.
                  </p>
                </div>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className={adminLabel} htmlFor="bf-tagline">Tagline</label>
              <input id="bf-tagline" className={adminInput} value={form.tagline}
                onChange={(e) => set("tagline", e.target.value)} placeholder="A short line that describes the business" />
            </div>
            <div className="sm:col-span-2">
              <label className={adminLabel} htmlFor="bf-desc">About the business</label>
              <textarea id="bf-desc" rows={4} maxLength={1000} value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="What the business offers and what makes it different…"
                className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100" />
            </div>
          </div>

          <div className="h-px w-full bg-background-200" />

          {/* ── Classification ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={adminLabel} htmlFor="bf-category">Category *</label>
              <select id="bf-category" className={SELECT_CLASS} value={form.categoryId}
                onChange={(e) => onCategoryChange(e.target.value)} required>
                <option value="">Select category</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-subcategory">Sub category</label>
              <select id="bf-subcategory" className={SELECT_CLASS} value={form.subcategoryId}
                disabled={!form.categoryId || subOptions.length === 0}
                onChange={(e) => set("subcategoryId", e.target.value)}>
                <option value="">{subOptions.length === 0 ? "No sub categories" : "Select sub category"}</option>
                {subOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-district">District *</label>
              <select id="bf-district" className={SELECT_CLASS} value={form.districtId}
                onChange={(e) => onDistrictChange(e.target.value)} required>
                <option value="">Select district</option>
                {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-town">Town / city</label>
              <select id="bf-town" className={SELECT_CLASS} value={form.townId}
                disabled={!form.districtId || towns.length === 0}
                onChange={(e) => set("townId", e.target.value)}>
                <option value="">{towns.length === 0 ? "No towns" : "Select town"}</option>
                {towns.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-plan">Plan</label>
              <select id="bf-plan" className={SELECT_CLASS} value={form.planId}
                onChange={(e) => set("planId", e.target.value)}>
                <option value="">Free (default)</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-owner">Assign to owner</label>
              <select id="bf-owner" className={SELECT_CLASS} value={form.ownerId}
                onChange={(e) => set("ownerId", e.target.value)}>
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Status ── */}
          <div>
            <span className={adminLabel}>{isEdit ? "Status" : "Publish state"}</span>
            <div className="flex flex-wrap gap-2">
              {statusChoices.map((opt) => {
                const active = form.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => set("status", opt.value)}
                    aria-pressed={active}
                    className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border px-3.5 py-2 text-xs font-semibold transition-colors ${
                      active
                        ? "border-primary-400 bg-primary-50 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300"
                    }`}
                  >
                    <i className={`${opt.icon} text-sm`} /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-px w-full bg-background-200" />

          {/* ── Contact ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={adminLabel} htmlFor="bf-phone">Phone</label>
              <input id="bf-phone" type="tel" className={adminInput} value={form.phone}
                onChange={(e) => set("phone", e.target.value)} placeholder="+91 98450 00000" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-whatsapp">WhatsApp</label>
              <input id="bf-whatsapp" type="tel" className={adminInput} value={form.whatsapp}
                onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 98450 00000" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-email">Email</label>
              <input id="bf-email" type="email" className={adminInput} value={form.email}
                onChange={(e) => set("email", e.target.value)} placeholder="contact@business.in" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-website">Website</label>
              <input id="bf-website" type="url" className={adminInput} value={form.website}
                onChange={(e) => set("website", e.target.value)} placeholder="https://" />
            </div>
            <div className="sm:col-span-2">
              <label className={adminLabel} htmlFor="bf-address">Address</label>
              <input id="bf-address" className={adminInput} value={form.address}
                onChange={(e) => set("address", e.target.value)} placeholder="Shop / building, street, area" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-pin">PIN code</label>
              <input id="bf-pin" className={adminInput} value={form.pinCode}
                onChange={(e) => set("pinCode", e.target.value)} placeholder="575001" />
            </div>
            <div>
              <label className={adminLabel} htmlFor="bf-video">Video tour</label>
              <input id="bf-video" type="url" className={adminInput} value={form.videoUrl}
                onChange={(e) => set("videoUrl", e.target.value)} placeholder="https://www.youtube.com/watch?v=…" />
            </div>
          </div>

          <div className="h-px w-full bg-background-200" />

          {/* ── Photos ── */}
          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
            <h3 className="font-heading text-sm font-bold text-foreground-950">Photos</h3>
            <p className="mt-0.5 text-xs text-foreground-500">JPG, PNG or WebP — validated before upload.</p>

            <div className="mt-3 flex flex-col gap-4">
              <div>
                <span className={adminLabel}>Cover image</span>
                <ImageUploader
                  value={form.coverUrl ? [form.coverUrl] : []}
                  onChange={(urls) => set("coverUrl", urls[0] ?? "")}
                  rule={LISTING_COVER_RULE}
                  folder="listings"
                  token={token}
                  onBrowseLibrary={() => setPicker("cover")}
                />
              </div>
              <div>
                <span className={adminLabel}>Logo</span>
                <ImageUploader
                  value={form.logoUrl ? [form.logoUrl] : []}
                  onChange={(urls) => set("logoUrl", urls[0] ?? "")}
                  rule={LISTING_LOGO_RULE}
                  folder="logos"
                  token={token}
                  onBrowseLibrary={() => setPicker("logo")}
                />
              </div>
              <div>
                <span className={adminLabel}>Gallery ({form.gallery.length}/{photoLimit})</span>
                <ImageUploader
                  value={form.gallery}
                  onChange={(urls) => set("gallery", urls)}
                  rule={LISTING_GALLERY_RULE}
                  folder="gallery"
                  token={token}
                  multiple
                  maxFiles={galleryCap}
                  onBrowseLibrary={() => setPicker("gallery")}
                  browseLabel="Add from library"
                  capacityMessage={
                    <>
                      {selectedPlan ? `${selectedPlan.name} plan allows` : "The selected plan allows"}{" "}
                      up to {photoLimit} {photoLimit === 1 ? "photo" : "photos"}. Pick a higher plan to
                      upload more.
                    </>
                  }
                />
                {overPhotoLimit && (
                  <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                    <i className="ri-alert-line mt-0.5 shrink-0" />
                    This listing has {form.gallery.length} photos, which is more than its plan allows ({photoLimit}).
                    Remove the extra ones or move it to a bigger plan.
                  </p>
                )}
              </div>
            </div>
          </div>
        </form>
      </AdminModal>

      {picker && (
        <MediaLibraryModal
          token={token}
          canUpload
          uploadFolder={picker === "cover" ? "listings" : picker === "logo" ? "logos" : "gallery"}
          title={picker === "gallery" ? "Add from Media Library" : "Choose from Media Library"}
          multiple={picker === "gallery"}
          maxSelect={picker === "gallery" ? Math.max(1, galleryCap - form.gallery.length) : 1}
          currentUrls={
            picker === "cover"
              ? form.coverUrl ? [form.coverUrl] : []
              : picker === "logo"
                ? form.logoUrl ? [form.logoUrl] : []
                : form.gallery
          }
          onSelect={(urls) => {
            if (picker === "cover") set("coverUrl", urls[0] ?? "");
            else if (picker === "logo") set("logoUrl", urls[0] ?? "");
            else {
              const merged = [...form.gallery];
              for (const u of urls) if (!merged.includes(u)) merged.push(u);
              set("gallery", merged.slice(0, galleryCap));
            }
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
}